import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { ItemType } from '../types';

const TRACK_FIELDS = [
  { key: 'track_manufacturer', label: 'Manufacturer' },
  { key: 'track_model', label: 'Model' },
  { key: 'track_size', label: 'Size' },
  { key: 'track_serial_number', label: 'Serial Number' },
  { key: 'track_date_manufactured', label: 'Date Manufactured' },
  { key: 'track_condition', label: 'Condition' },
  { key: 'track_assigned_to', label: 'Assigned To' },
  { key: 'track_station', label: 'Station' },
  { key: 'track_notes', label: 'Notes' },
];

export default function ItemTypes() {
  const [types, setTypes] = useState<ItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFields, setNewFields] = useState<Record<string, boolean>>(
    Object.fromEntries(TRACK_FIELDS.map(f => [f.key, true]))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchTypes = async () => {
    try {
      const data = await api.get<ItemType[]>('/item-types');
      setTypes(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleToggle = async (type: ItemType, field: string) => {
    try {
      const updated = await api.put<ItemType>(`/item-types/${type.id}`, {
        [field]: !(type as any)[field],
      });
      setTypes(ts => ts.map(t => t.id === type.id ? updated : t));
    } catch (e) { console.error(e); }
  };

  const handleQtyChange = async (type: ItemType, field: 'required_frontline_qty' | 'required_backup_qty', value: number) => {
    try {
      const updated = await api.put<ItemType>(`/item-types/${type.id}`, {
        [field]: Math.max(0, value),
      });
      setTypes(ts => ts.map(t => t.id === type.id ? updated : t));
    } catch (e) { console.error(e); }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/item-types', { name: newName.trim(), ...newFields });
      setShowAdd(false);
      setNewName('');
      fetchTypes();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#111' }}>Item Types</h1>
        <button onClick={() => setShowAdd(true)}
          style={{ background: '#b91c1c', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          + Add Type
        </button>
      </div>

      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Toggle which fields to track for each item type. Changes apply immediately.
      </p>

      {types.map(type => (
        <div key={type.id} style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>
            {type.name}
            {type.is_default && <span style={{ marginLeft: 8, fontSize: 11, color: '#666', fontWeight: 400 }}>Default</span>}
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TRACK_FIELDS.map(f => {
              const active = (type as any)[f.key];
              return (
                <button key={f.key} onClick={() => handleToggle(type, f.key)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                    border: active ? '1px solid #16a34a' : '1px solid #d1d5db',
                    background: active ? '#f0fdf4' : 'white',
                    color: active ? '#16a34a' : '#666',
                    fontWeight: active ? 600 : 400,
                  }}>
                  {active ? '\u2713 ' : ''}{f.label}
                </button>
              );
            })}
          </div>

          {/* Required gear quantities */}
          <div style={{ display: 'flex', gap: 16, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Required per member:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <label style={{ fontSize: 13, color: '#374151' }}>Frontline</label>
              <input
                type="number"
                min={0}
                value={type.required_frontline_qty ?? 0}
                onChange={e => handleQtyChange(type, 'required_frontline_qty', parseInt(e.target.value) || 0)}
                style={{ width: 52, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, textAlign: 'center' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <label style={{ fontSize: 13, color: '#374151' }}>Backup</label>
              <input
                type="number"
                min={0}
                value={type.required_backup_qty ?? 0}
                onChange={e => handleQtyChange(type, 'required_backup_qty', parseInt(e.target.value) || 0)}
                style={{ width: 52, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, textAlign: 'center' }}
              />
            </div>
          </div>
        </div>
      ))}

      {/* Add Type Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={() => setShowAdd(false)}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 440, width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>Add Custom Item Type</h2>

            {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Type Name *</label>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                placeholder="e.g. SCBA, Radio, Flashlight..." />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Fields to Track</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {TRACK_FIELDS.map(f => (
                  <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={newFields[f.key]}
                      onChange={e => setNewFields(nf => ({ ...nf, [f.key]: e.target.checked }))} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAdd(false)} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={handleAdd} disabled={saving || !newName.trim()}
                style={{ padding: '10px 20px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: (saving || !newName.trim()) ? 0.7 : 1 }}>
                {saving ? 'Adding...' : 'Add Type'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
